/**
 * Player-facing settings: sound, appearance and accent, DAS/ARR, the ghost
 * piece, the high-contrast flag. Plain reactive state (Svelte 5 runes work outside
 * `.svelte` files in a `.svelte.ts` module), persisted to `localStorage` so
 * a reload keeps them — solo play works fully offline (BASELINE 5), and that
 * includes not losing your settings when the network is what's missing.
 *
 * The load/validate/serialise logic itself lives in `settings-io.ts`, which
 * has no rune in it and is what the test suite exercises directly — see the
 * comment at the top of that file for why.
 */
import { loadInitialSettings, validateSettings, writeSettings } from './settings-io.js';
import type { Settings } from './settings-io.js';

export type { Settings } from './settings-io.js';

export const settings: Settings = $state(loadInitialSettings());

/**
 * Anything that wants to know when settings changed.
 *
 * One subscriber exists today — the account store, which pushes the blob to
 * the server so `docs/DESIGN.md`'s "settings (DAS/ARR, theme, etc.) synced"
 * is true. It is a callback list rather than an import from
 * `account.svelte.ts` because the dependency has to point the other way: this
 * module is loaded by every screen, including a guest's, and an offline
 * install must not pull the account code in to change a theme.
 *
 * An array rather than a `Set`, because `svelte/prefer-svelte-reactivity`
 * rightly objects to a plain `Set` in a `.svelte.ts` file — and the reactive
 * one would be wrong here: this list is not state anything renders, it is a
 * subscription table with one entry in it.
 */
type SettingsListener = (current: Settings) => void;
let listeners: readonly SettingsListener[] = [];

export function onSettingsPersisted(listener: SettingsListener): () => void {
  listeners = [...listeners, listener];
  return () => {
    listeners = listeners.filter((entry) => entry !== listener);
  };
}

export function persistSettings(): void {
  writeSettings(settings);
  for (const listener of listeners) listener(settings);
}

/**
 * Adopt settings that came from somewhere else — today, the account a person
 * just signed in on.
 *
 * Field by field rather than by replacing the object, because `settings` is
 * the `$state` proxy every screen already reads; reassigning the binding would
 * leave them all pointed at the old one. Unknown keys are ignored, so a blob
 * written by a newer build cannot put a screen into a state it cannot render.
 */
export function adoptSettings(incoming: unknown): void {
  if (typeof incoming !== 'object' || incoming === null) return;
  const validated = validateSettings(incoming);
  settings.dasMs = validated.dasMs;
  settings.arrMs = validated.arrMs;
  settings.ghost = validated.ghost;
  settings.highContrast = validated.highContrast;
  settings.musicOn = validated.musicOn;
  settings.musicVolume = validated.musicVolume;
  settings.sfxOn = validated.sfxOn;
  settings.sfxVolume = validated.sfxVolume;
  settings.appearance = validated.appearance;
  settings.accent = validated.accent;
  writeSettings(settings);
}
